import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await register(formData);

      if (!result.success) {
        setError(result.message);
        return;
      }

      navigate('/login');
    } catch (err) {
      setError('Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const strength = (() => {
    const p = formData.password;
    if (!p) return { text: '', width: '0%', color: '#22c55e' };
    if (p.length < 6) return { text: 'Weak', width: '30%', color: '#ef4444' };
    if (p.length < 10) return { text: 'Medium', width: '65%', color: '#f59e0b' };
    return { text: 'Strong', width: '100%', color: '#22c55e' };
  })();

  return (
    <div style={styles.page}>

      {/* 🌊 SAFE ANIMATED BACKGROUND (NO JS ERROR) */}
      <style>{`
        @keyframes float1 {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(30px,20px) scale(1.08); }
        }

        @keyframes float2 {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(-30px,-20px) scale(1.1); }
        }

        @keyframes float3 {
          0%,100% { transform: translate(-50%,-50%) scale(1); }
          50% { transform: translate(-50%,-50%) scale(1.05); }
        }
      `}</style>

      {/* 🌫️ BACKGROUND LAYERS */}
      <div style={styles.blurOne} />
      <div style={styles.blurTwo} />
      <div style={styles.blurThree} />

      {/* LEFT PANEL (desktop only) */}
      {!isMobile && (
        <div style={styles.leftPanel}>
          <div style={styles.overlay} />

          <div style={styles.hero}>
            <h1 style={styles.heroTitle}>
              Start Building<br />Faster Today
            </h1>

            <p style={styles.heroText}>
              Enterprise SaaS platform for automation, cloud & analytics.
            </p>
          </div>
        </div>
      )}

      {/* RIGHT CENTER PANEL */}
      <div style={styles.rightPanel}>

        <div style={styles.card}>

          <h1 style={styles.title}>Create Account</h1>
          <p style={styles.subtitle}>Join the platform in seconds</p>

          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>

            <input
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              style={styles.input}
              required
            />

            <input
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              style={styles.input}
              required
            />

            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              style={styles.input}
              required
            />

            {/* PASSWORD STRENGTH */}
            <div style={styles.strengthWrap}>
              <div
                style={{
                  ...styles.strengthBar,
                  width: strength.width,
                  background: strength.color
                }}
              />
            </div>

            <span style={styles.strengthText}>
              {strength.text}
            </span>

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

          </form>

          <div style={styles.bottom}>
            Already have an account?{' '}
            <Link to="/login" style={styles.link}>
              Sign in
            </Link>
          </div>

        </div>

      </div>
    </div>
  );
}

export default Register;

/* ================= PREMIUM STYLES ================= */

const styles = {

  page: {
    minHeight: '100vh',
    display: 'flex',
    background: '#020617',
    fontFamily: 'Inter, sans-serif',
    position: 'relative',
    overflow: 'hidden'
  },

  /* 🌫️ APPLE STYLE BLUR BACKGROUND */
  blurOne: {
    position: 'absolute',
    width: 650,
    height: 650,
    top: -250,
    left: -250,
    borderRadius: '50%',
    background: 'radial-gradient(circle, #6366f1 0%, transparent 60%)',
    filter: 'blur(120px)',
    opacity: 0.45,
    animation: 'float1 14s ease-in-out infinite'
  },

  blurTwo: {
    position: 'absolute',
    width: 650,
    height: 650,
    bottom: -280,
    right: -250,
    borderRadius: '50%',
    background: 'radial-gradient(circle, #0ea5e9 0%, transparent 60%)',
    filter: 'blur(140px)',
    opacity: 0.35,
    animation: 'float2 16s ease-in-out infinite'
  },

  blurThree: {
    position: 'absolute',
    width: 500,
    height: 500,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    borderRadius: '50%',
    background: 'radial-gradient(circle, #8b5cf6 0%, transparent 65%)',
    filter: 'blur(160px)',
    opacity: 0.18,
    animation: 'float3 18s ease-in-out infinite'
  },

  /* LEFT PANEL */
  leftPanel: {
    flex: 1,
    position: 'relative',
    background:
      'url(https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1800&q=80)',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  },

  overlay: {
    position: 'absolute',
    inset: 0,
    background:
      'linear-gradient(135deg, rgba(2,6,23,.95), rgba(15,23,42,.7))'
  },

  hero: {
    position: 'relative',
    zIndex: 2,
    padding: 80,
    color: '#fff',
    maxWidth: 520
  },

  heroTitle: {
    fontSize: 60,
    fontWeight: 900,
    lineHeight: 1.1
  },

  heroText: {
    marginTop: 16,
    color: '#cbd5e1'
  },

  /* CENTER PANEL (FIXED) */
  rightPanel: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    position: 'relative',
    zIndex: 10
  },

  /* GLASS CARD */
  card: {
    width: '100%',
    maxWidth: 460,
    padding: 42,
    borderRadius: 32,
    background: 'rgba(255,255,255,.08)',
    border: '1px solid rgba(255,255,255,.12)',
    backdropFilter: 'blur(40px)',
    WebkitBackdropFilter: 'blur(40px)',
    boxShadow: '0 40px 120px rgba(0,0,0,.6)',
    color: '#fff'
  },

  title: {
    fontSize: 34,
    fontWeight: 900
  },

  subtitle: {
    color: '#94a3b8',
    marginTop: 6,
    marginBottom: 20
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },

  input: {
    height: 56,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,.12)',
    background: 'rgba(255,255,255,.05)',
    color: '#fff',
    padding: '0 16px',
    fontSize: 15,
    outline: 'none'
  },

  strengthWrap: {
    height: 5,
    background: 'rgba(255,255,255,.1)',
    borderRadius: 999,
    overflow: 'hidden'
  },

  strengthBar: {
    height: '100%',
    transition: '0.3s'
  },

  strengthText: {
    fontSize: 12,
    color: '#94a3b8'
  },

  button: {
    height: 58,
    borderRadius: 18,
    border: 'none',
    fontWeight: 800,
    color: '#fff',
    background: 'linear-gradient(135deg,#6366f1,#3b82f6,#06b6d4)'
  },

  bottom: {
    marginTop: 18,
    textAlign: 'center',
    color: '#94a3b8'
  },

  link: {
    color: '#60a5fa',
    fontWeight: 700,
    textDecoration: 'none'
  },

  error: {
    marginBottom: 14,
    padding: 12,
    borderRadius: 12,
    background: 'rgba(239,68,68,.15)',
    border: '1px solid rgba(239,68,68,.3)',
    color: '#fecaca'
  }
};